import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'

import { WorkflowExecutionStatus } from '@/gen/cortado/v1/cortado_pb'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { WorkflowExecutionsTable } from '@/components/settings/workflows/workflow-executions-table'
import { WorkflowsListSection } from '@/components/settings/workflows/workflows-list-section'
import { useBreadcrumbFromRoute } from '@/hooks/use-breadcrumb-from-route'

type WorkflowsView = 'active' | 'definitions'

// Module-level constant so the prop is a stable reference across renders. An
// inline array literal would be a fresh reference each render, retriggering the
// table's reset effect and wiping the user's "load more" progress.
const ACTIVE_RUN_STATUSES = [
  WorkflowExecutionStatus.RUNNING,
  WorkflowExecutionStatus.PENDING,
]

export const Route = createFileRoute('/_app/_home/workflows/')({
  component: WorkflowsControlTower,
  validateSearch: z.object({
    view: z.enum(['active', 'definitions']).optional(),
  }),
})

function WorkflowsControlTower() {
  useBreadcrumbFromRoute()
  const navigate = useNavigate()
  const { view = 'active' } = Route.useSearch()
  return (
    <div className="flex flex-col gap-4 p-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Workflows</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Monitor in-flight automation runs across your organization and manage your
          workflow definitions.
        </p>
      </div>

      <Tabs
        value={view}
        onValueChange={(value) =>
          navigate({ to: '/workflows', search: { view: value as WorkflowsView } })
        }
      >
        <TabsList>
          <TabsTrigger value="active">Active runs</TabsTrigger>
          <TabsTrigger value="definitions">Workflows</TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="mt-4">
          <WorkflowExecutionsTable
            statuses={ACTIVE_RUN_STATUSES}
            showStopAction
            showWaitingColumn
            showDisplayContext
          />
        </TabsContent>

        <TabsContent value="definitions" className="mt-4">
          <WorkflowsListSection />
        </TabsContent>
      </Tabs>
    </div>
  )
}
